// SubmitFeedbackPanel.tsx — 1.21.50 right-side slide-in submit experience.
// Replaces the old centered SubmitFeedbackDialog. The board stays visible
// (dimmed) behind the panel; Escape/backdrop-click close with a draft-discard
// confirm step when the form is dirty.
import { useCallback, useEffect, useRef, useState } from 'react';
import type { ChangeEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { Sheet, Button, Switch, TextField, Textarea, useToast } from '@/ui';
import { Icon } from '@/icons';
import { ApiError } from '@/core/lib/apiClient';
import { useFeedbackSearch } from '@/hooks/useFeedback';
import { feedbackService } from '@/core/services/feedbackService';
import type {
  CreateFeedbackInput,
  FeedbackCategory,
  FeedbackDeviceSnapshot,
  FeedbackFrequency,
  FeedbackItem,
  FeedbackPlatform,
} from '@/core/models/feedback';
import {
  FEEDBACK_CATEGORY_LABELS,
  FEEDBACK_CATEGORIES,
  FEEDBACK_PLATFORM_LABELS,
} from '@/core/models/feedback';
import {
  BODY_META,
  CATEGORY_ICONS,
  CATEGORY_TONE_COLOR,
  attachmentUploadErrorMessage,
  describeFeedbackFieldError,
} from './feedbackMeta';
import { StatusBadge } from './StatusBadge';
import { MarkdownEditor } from './MarkdownEditor';
import {
  DEVICE_SNAPSHOT_FIELD_LABELS,
  DEVICE_SNAPSHOT_FIELD_ORDER,
  captureDeviceSnapshot,
  captureLocalTimezoneLabel,
  parseUserAgent,
} from './lib/deviceSnapshot';

// Mirrors the backend's per-item cap and per-file size limit.
const MAX_ATTACHMENTS = 5;
const MAX_ATTACHMENT_SIZE_BYTES = 15 * 1024 * 1024;
const ATTACHMENT_ACCEPT =
  '.png,.jpg,.jpeg,.webp,.gif,.mp4,.pdf,.txt,image/png,image/jpeg,image/webp,image/gif,video/mp4,application/pdf,text/plain';
const TITLE_MAX = 140;
// Mirrors backend BODY_MAX (api.py).
const BODY_MAX = 4000;
const BUG_PERF_CATEGORIES: FeedbackCategory[] = ['bug', 'performance'];

/** Names + messages a field-level 400 on one of the NEW structured columns —
 * these aren't part of feedbackMeta.FEEDBACK_TOPIC_FIELDS (the legacy
 * per-category `details` schema), so describeFeedbackFieldError alone can't
 * recognize them. Checked first; falls back to describeFeedbackFieldError for
 * anything else the server might reject. */
const STRUCTURED_FIELD_LABELS: Record<string, string> = {
  expected_behavior: 'Expected behavior',
  actual_behavior: 'Actual behavior',
  steps_to_reproduce: 'Steps to reproduce',
  frequency: 'How often does it happen?',
  additional_context: 'Additional context',
};

function describeStructuredFieldError(err: unknown): string | null {
  if (!(err instanceof ApiError) || !err.fields) return null;
  for (const [key, label] of Object.entries(STRUCTURED_FIELD_LABELS)) {
    const code = err.fields[key];
    if (typeof code === 'string') {
      return `"${label}" ${code === 'too_long' ? 'is too long' : "isn't valid"}.`;
    }
  }
  return null;
}
const FREQUENCY_OPTIONS: { value: FeedbackFrequency; label: string }[] = [
  { value: 'always', label: 'Always' },
  { value: 'sometimes', label: 'Sometimes' },
  { value: 'rarely', label: 'Rarely' },
  { value: 'once', label: 'Once' },
];

// ── styled pieces ────────────────────────────────────────────────────────────

const PanelBody = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
`;

// Wraps the actual form (fields + actions) so it can be made `inert` while
// the discard-confirm overlay is up (W4) — everything behind the overlay
// must stop being focusable/interactive until it's dismissed.
const FormContent = styled.div`
  display: flex;
  flex-direction: column;
`;

const Fields = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;
`;

const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const FieldLabel = styled.span`
  font-family: ${({ theme }) => theme.font.body};
  font-size: ${({ theme }) => theme.fontSize.sm};
  font-weight: 600;
  color: ${({ theme }) => theme.color.textPrimary};
`;

const Topics = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

const Topic = styled.button<{ $on: boolean; $color: string }>`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 7px 12px;
  cursor: pointer;
  border-radius: ${({ theme }) => theme.radius.md};
  font-family: ${({ theme }) => theme.font.body};
  font-size: ${({ theme }) => theme.fontSize.xs};
  font-weight: 600;
  border: 1px solid ${({ theme, $on }) => ($on ? 'transparent' : theme.color.glassBorder)};
  background: ${({ $on, $color }) => ($on ? `${$color}22` : 'transparent')};
  color: ${({ theme, $on, $color }) => ($on ? $color : theme.color.textPrimary)};

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.color.focusRing};
    outline-offset: 2px;
  }
`;

const Note = styled.p`
  margin: 0;
  font-family: ${({ theme }) => theme.font.body};
  font-size: ${({ theme }) => theme.fontSize.xs};
  color: ${({ theme }) => theme.color.textMuted};
`;

const Actions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 18px;
`;

const ErrorText = styled.p`
  margin: 0;
  font-family: ${({ theme }) => theme.font.body};
  font-size: ${({ theme }) => theme.fontSize.sm};
  color: ${({ theme }) => theme.color.danger};
`;

const Disclosure = styled.details`
  border: 1px solid ${({ theme }) => theme.color.glassBorder};
  border-radius: ${({ theme }) => theme.radius.md};
  padding: 10px 12px;

  &[open] {
    padding-bottom: 12px;
  }
`;

const DisclosureSummary = styled.summary`
  cursor: pointer;
  list-style: none;
  display: flex;
  align-items: center;
  gap: 6px;
  font-family: ${({ theme }) => theme.font.body};
  font-size: ${({ theme }) => theme.fontSize.sm};
  font-weight: 600;
  color: ${({ theme }) => theme.color.textPrimary};

  &::-webkit-details-marker {
    display: none;
  }
  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.color.focusRing};
    outline-offset: 2px;
  }
`;

const DisclosureChevron = styled.span`
  display: inline-flex;
  transition: transform 0.15s ease;

  details[open] > summary & {
    transform: rotate(180deg);
  }
`;

const DisclosureBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;
  margin-top: 12px;
`;

const RadioRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

const RadioOption = styled.label<{ $on: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 7px 12px;
  cursor: pointer;
  border-radius: ${({ theme }) => theme.radius.md};
  font-family: ${({ theme }) => theme.font.body};
  font-size: ${({ theme }) => theme.fontSize.xs};
  font-weight: 600;
  border: 1px solid ${({ theme, $on }) => ($on ? 'transparent' : theme.color.glassBorder)};
  background: ${({ theme, $on }) => ($on ? `${theme.color.brandFrom}22` : 'transparent')};
  color: ${({ theme, $on }) => ($on ? theme.color.brandFrom : theme.color.textPrimary)};

  input {
    position: absolute;
    opacity: 0;
    pointer-events: none;
  }
  &:focus-within {
    outline: 2px solid ${({ theme }) => theme.color.focusRing};
    outline-offset: 2px;
  }
`;

const DupBox = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 10px 12px;
  border: 1px dashed ${({ theme }) => theme.color.glassBorder};
  border-radius: ${({ theme }) => theme.radius.md};
`;

const DupLabel = styled.span`
  display: flex;
  align-items: center;
  gap: 6px;
  font-family: ${({ theme }) => theme.font.body};
  font-size: ${({ theme }) => theme.fontSize.sm};
  font-weight: 600;
  color: ${({ theme }) => theme.color.textPrimary};
`;

const DupResults = styled.ul`
  margin: 0;
  padding: 0;
  list-style: none;
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const DupResult = styled.li`
  display: flex;
`;

const DupResultButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  width: 100%;
  padding: 7px 10px;
  cursor: pointer;
  text-align: left;
  border: 1px solid ${({ theme }) => theme.color.glassBorder};
  border-radius: ${({ theme }) => theme.radius.sm};
  background: transparent;
  color: ${({ theme }) => theme.color.textPrimary};
  font-family: ${({ theme }) => theme.font.body};
  font-size: ${({ theme }) => theme.fontSize.xs};

  &:hover {
    background: ${({ theme }) => theme.color.hover};
  }
  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.color.focusRing};
    outline-offset: 2px;
  }
`;

const DupTitle = styled.span`
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const PlatformSelect = styled.select`
  padding: 8px 10px;
  border-radius: ${({ theme }) => theme.radius.md};
  border: 1px solid ${({ theme }) => theme.color.glassBorder};
  background: ${({ theme }) => theme.color.surface};
  color: ${({ theme }) => theme.color.textPrimary};
  font-family: ${({ theme }) => theme.font.body};
  font-size: ${({ theme }) => theme.fontSize.sm};
`;

const AttachList = styled.ul`
  margin: 0;
  padding: 0;
  list-style: none;
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const AttachRow = styled.li`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 6px 10px;
  border: 1px solid ${({ theme }) => theme.color.glassBorder};
  border-radius: ${({ theme }) => theme.radius.sm};
  font-family: ${({ theme }) => theme.font.body};
  font-size: ${({ theme }) => theme.fontSize.xs};
  color: ${({ theme }) => theme.color.textPrimary};
`;

const AttachName = styled.span`
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const AttachRemoveButton = styled.button`
  flex: none;
  display: inline-flex;
  align-items: center;
  padding: 2px;
  border: none;
  background: transparent;
  color: ${({ theme }) => theme.color.textMuted};
  cursor: pointer;

  &:hover {
    color: ${({ theme }) => theme.color.danger};
  }
  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.color.focusRing};
    outline-offset: 2px;
  }
`;

const AttachButton = styled(Button)`
  align-self: flex-start;
`;

const DeviceRow = styled.div`
  display: flex;
  align-items: flex-end;
  gap: 6px;
`;

const SnapshotList = styled.ul`
  margin: 0;
  padding: 0;
  list-style: none;
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const SnapshotRow = styled.li`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 5px 10px;
  border: 1px solid ${({ theme }) => theme.color.glassBorder};
  border-radius: ${({ theme }) => theme.radius.sm};
  font-family: ${({ theme }) => theme.font.body};
  font-size: ${({ theme }) => theme.fontSize.xs};
  color: ${({ theme }) => theme.color.textPrimary};
`;

const RemoveFieldButton = styled.button`
  flex: none;
  display: inline-flex;
  align-items: center;
  padding: 2px;
  border: none;
  background: transparent;
  color: ${({ theme }) => theme.color.textMuted};
  cursor: pointer;

  &:hover {
    color: ${({ theme }) => theme.color.danger};
  }
  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.color.focusRing};
    outline-offset: 2px;
  }
`;

// position: fixed (not absolute) — an absolutely-positioned overlay sized to
// the PANEL CONTENT's scroll height would center its message somewhere deep
// inside a tall/scrolled form, well outside the visible viewport (2026-08-10
// second adversarial review, finding W4). Fixed to the viewport itself, it
// always covers exactly what's on screen regardless of how tall or scrolled
// the form behind it is. z-index above Sheet's own surface (201).
const DiscardOverlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 250;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 14px;
  padding: 24px;
  text-align: center;
  background: ${({ theme }) => theme.color.glassBg};
  backdrop-filter: blur(6px);
`;

const DiscardMessage = styled.p`
  margin: 0;
  font-family: ${({ theme }) => theme.font.body};
  font-size: ${({ theme }) => theme.fontSize.sm};
  color: ${({ theme }) => theme.color.textPrimary};
`;

const DiscardActions = styled.div`
  display: flex;
  gap: 10px;
`;

// ── component ─────────────────────────────────────────────────────────────────

export interface SubmitFeedbackPanelProps {
  initialCategory?: FeedbackCategory;
  /** Pre-filled from a link (1.27.50: the video player's "Report playback issue"). */
  initialTitle?: string;
  initialBody?: string;
  open: boolean;
  onClose: () => void;
  onSubmit: (input: CreateFeedbackInput) => Promise<FeedbackItem>;
  /** See SubmitFeedbackDialog's original docstring — MUST run AFTER every
   * attachment upload has settled. */
  onReconcileAfterCreate: (item: FeedbackItem) => Promise<void>;
}

export function SubmitFeedbackPanel({
  initialCategory = 'bug',
  initialTitle = '',
  initialBody = '',
  open,
  onClose,
  onSubmit,
  onReconcileAfterCreate,
}: SubmitFeedbackPanelProps) {
  const toast = useToast();
  const navigate = useNavigate();
  const [title, setTitle] = useState(initialTitle);
  const [body, setBody] = useState(initialBody);
  const [category, setCategory] = useState<FeedbackCategory>(initialCategory);
  const [platform, setPlatform] = useState<FeedbackPlatform>('unspecified');
  const [discOpen, setDiscOpen] = useState(false);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [confirmDiscardOpen, setConfirmDiscardOpen] = useState(false);

  // ── structured fields (1.21.50) ─────────────────────────────────────────
  const [expectedBehavior, setExpectedBehavior] = useState('');
  const [actualBehavior, setActualBehavior] = useState('');
  const [stepsToReproduce, setStepsToReproduce] = useState('');
  const [frequency, setFrequency] = useState<FeedbackFrequency>('');
  const [additionalContext, setAdditionalContext] = useState('');

  // ── device & environment block — captured once per panel-open, privacy-
  // critical (task spec §5): lives ONLY in this component's state, sent ONLY
  // inside the submit POST when not excluded, never stored client-side.
  const [devicePlatform, setDevicePlatform] = useState('');
  const [browser, setBrowser] = useState('');
  const [osVersion, setOsVersion] = useState('');
  const [submittedTz, setSubmittedTz] = useState('');
  const [snapshot, setSnapshot] = useState<FeedbackDeviceSnapshot>({});
  const [excludeDeviceInfo, setExcludeDeviceInfo] = useState(false);

  // Attach step: files staged locally, uploaded AFTER the item exists
  // (create -> upload staged files -> reconcileAfterCreate contract).
  const [pendingFiles, setPendingFiles] = useState<File[]>([]);
  const [attachError, setAttachError] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);
  // Inline images inserted via the markdown editor — uploaded immediately as
  // drafts (item=null). Tracked as {id, url} (not just id) so submit-time
  // can tell which ones are STILL referenced in the body text (2026-08-10
  // second adversarial review, finding W2): deleting the markdown for an
  // inline image must not silently keep publishing it — only ids whose url
  // still appears in `body` are claimed on create(); everything else gets
  // revoked via deleteDraftAttachment (see revokeDrafts below).
  const [insertedDrafts, setInsertedDrafts] = useState<{ id: number; url: string }[]>([]);

  const { results: dupResults, searching: dupSearching } = useFeedbackSearch(title);

  const isBugOrPerf = BUG_PERF_CATEGORIES.includes(category);

  // Capture the device snapshot + parsed UA exactly once per panel-open
  // transition — never on a timer, never re-read while the panel stays open.
  // One-shot capture-on-open, not a derived/fetched value (same precedent as
  // FeedbackDetailPage's assignDraft sync).
  useEffect(() => {
    if (!open) return;
    const parsed = parseUserAgent(navigator.userAgent ?? '');
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setSnapshot(captureDeviceSnapshot());
    setDevicePlatform(parsed.devicePlatform);
    setBrowser(parsed.browser);
    setOsVersion(parsed.osVersion);
    setSubmittedTz(captureLocalTimezoneLabel());
  }, [open]);

  // Focus management for the discard-confirm overlay (2026-08-10 second
  // adversarial review, finding W4): focus moves INTO the alertdialog (onto
  // its safe default action, "Keep editing") the moment it opens, and back
  // to whatever was focused beforehand once it closes either way (Keep
  // editing, Discard, or Escape-as-keep-editing).
  const keepEditingButtonRef = useRef<HTMLButtonElement>(null);
  const previouslyFocusedRef = useRef<HTMLElement | null>(null);
  useEffect(() => {
    if (confirmDiscardOpen) {
      previouslyFocusedRef.current = document.activeElement as HTMLElement | null;
      keepEditingButtonRef.current?.focus();
    } else {
      previouslyFocusedRef.current?.focus();
    }
  }, [confirmDiscardOpen]);

  function reset() {
    setTitle('');
    setBody('');
    setCategory(initialCategory);
    setPlatform('unspecified');
    setDiscOpen(false);
    setError('');
    setPendingFiles([]);
    setAttachError('');
    setInsertedDrafts([]);
    setExpectedBehavior('');
    setActualBehavior('');
    setStepsToReproduce('');
    setFrequency('');
    setAdditionalContext('');
    setExcludeDeviceInfo(false);
    setConfirmDiscardOpen(false);
  }

  const isDirty =
    title.trim() !== '' ||
    body.trim() !== '' ||
    expectedBehavior.trim() !== '' ||
    actualBehavior.trim() !== '' ||
    stepsToReproduce.trim() !== '' ||
    additionalContext.trim() !== '' ||
    pendingFiles.length > 0 ||
    insertedDrafts.length > 0;

  /** Fire-and-forget revoke — a failure here must never block the panel
   * from closing (W2). Used whenever inserted drafts are being abandoned:
   * unreferenced-at-submit, discard-confirmed, or duplicate-navigate. */
  function revokeDrafts(ids: number[]) {
    for (const id of ids) {
      feedbackService.deleteDraftAttachment(id).catch(() => {});
    }
  }

  function doClose() {
    reset();
    onClose();
  }

  function handleConfirmedDiscard() {
    revokeDrafts(insertedDrafts.map((d) => d.id));
    doClose();
  }

  // requestClose is handed to <Sheet onClose>. Originally built this way
  // because Sheet's own focus-trap effect depended on its onClose prop's
  // identity (re-running — and re-stealing focus onto the panel surface —
  // every time that identity changed): a plain closure recreated on every
  // render here would change identity on every keystroke (this component
  // re-renders on every field edit), which yanked focus out from under the
  // active input after the FIRST character of any field.
  //
  // fix/dialog-focus-loss fixed that root cause AT SOURCE in
  // ui/Sheet/Sheet.tsx's useFocusTrap (onClose is read from a ref inside the
  // effect now, not a dependency), so Sheet no longer needs a stable-identity
  // onClose to avoid stealing focus. This useCallback(…, [])/ref machinery
  // is kept anyway — deliberately, not because Sheet still requires it — for
  // two reasons: (1) it's what encodes the "Escape while the discard-confirm
  // is already showing means keep editing" behavior below (2026-08-10 second
  // adversarial review, finding W4), which has nothing to do with Sheet's
  // focus trap and would need equivalent latest-state plumbing regardless;
  // (2) belt-and-braces — a second layer of protection against the same
  // class of bug, at effectively zero cost. isDirtyRef/doCloseRef/
  // confirmDiscardOpenRef are refreshed in an effect (never written during
  // render — refs must not be mutated at render time) so requestClose
  // itself, built once via useCallback(…, []), can stay permanently stable
  // while still always reading the LATEST state whenever it's actually
  // invoked from an event handler.
  const isDirtyRef = useRef(isDirty);
  const doCloseRef = useRef(doClose);
  const confirmDiscardOpenRef = useRef(confirmDiscardOpen);
  useEffect(() => {
    isDirtyRef.current = isDirty;
    doCloseRef.current = doClose;
    confirmDiscardOpenRef.current = confirmDiscardOpen;
  });
  const requestClose = useCallback(() => {
    // Escape/backdrop-click while the discard confirm is ALREADY showing
    // means "keep editing", not "ask again" (2026-08-10 second adversarial
    // review, finding W4) — without this branch, Sheet's own Escape handler
    // calls this same requestClose on every press, and since the form is
    // still dirty it just re-set confirmDiscardOpen to the value it already
    // had: Escape looked like it did nothing.
    if (confirmDiscardOpenRef.current) {
      setConfirmDiscardOpen(false);
      return;
    }
    if (isDirtyRef.current) {
      setConfirmDiscardOpen(true);
    } else {
      doCloseRef.current();
    }
  }, []);

  const totalAttachments = pendingFiles.length + insertedDrafts.length;

  function handlePickFiles(e: ChangeEvent<HTMLInputElement>) {
    const picked = Array.from(e.target.files ?? []);
    e.target.value = '';
    if (picked.length === 0) return;

    const room = MAX_ATTACHMENTS - totalAttachments;
    if (room <= 0) {
      setAttachError(`You can attach up to ${MAX_ATTACHMENTS} files.`);
      return;
    }
    const oversized = picked.some((f) => f.size > MAX_ATTACHMENT_SIZE_BYTES);
    const accepted = picked.filter((f) => f.size <= MAX_ATTACHMENT_SIZE_BYTES).slice(0, room);
    const overRoom = picked.length > room && !oversized;

    setAttachError(
      oversized
        ? 'File too large (max 15 MB).'
        : overRoom
          ? `You can attach up to ${MAX_ATTACHMENTS} files.`
          : '',
    );
    setPendingFiles([...pendingFiles, ...accepted]);
  }

  function removePendingFile(index: number) {
    setPendingFiles((prev) => prev.filter((_, i) => i !== index));
  }

  async function handleInsertImage(file: File): Promise<{ url: string; alt: string } | null> {
    // Distinct, specific messages reusing the SAME copy the staged-file path
    // already shows (2026-08-10 second adversarial review, finding W5) —
    // thrown (not returned null) so MarkdownEditor can surface the real
    // reason instead of one generic "could not upload" for every failure
    // mode.
    if (totalAttachments >= MAX_ATTACHMENTS) {
      throw new Error(`You can attach up to ${MAX_ATTACHMENTS} files.`);
    }
    if (file.size > MAX_ATTACHMENT_SIZE_BYTES) {
      throw new Error('File too large (max 15 MB).');
    }
    try {
      const draft = await feedbackService.uploadDraftAttachment(file);
      setInsertedDrafts((prev) => [...prev, { id: draft.id, url: draft.url }]);
      return { url: draft.url, alt: draft.filename };
    } catch (err) {
      throw new Error(
        attachmentUploadErrorMessage(err, 'Could not upload that image. Please try again.'),
        {
          cause: err,
        },
      );
    }
  }

  async function uploadPendingAttachments(item: FeedbackItem): Promise<{
    failures: number;
    firstError: string | null;
  }> {
    let failures = 0;
    let firstError: string | null = null;
    for (const file of pendingFiles) {
      try {
        await feedbackService.uploadAttachment(item.id, file);
      } catch (err) {
        failures += 1;
        if (!firstError) {
          firstError = attachmentUploadErrorMessage(err, `Could not upload "${file.name}".`);
        }
      }
    }
    return { failures, firstError };
  }

  function openDuplicate(id: string) {
    // Every inline image inserted so far is abandoned along with the rest
    // of the draft (W2) — this navigates away without ever submitting.
    revokeDrafts(insertedDrafts.map((d) => d.id));
    reset();
    onClose();
    navigate(`/feedback/${id}`);
  }

  function removeSnapshotField(key: keyof FeedbackDeviceSnapshot) {
    setSnapshot((prev) => {
      const next = { ...prev };
      delete next[key];
      return next;
    });
  }

  async function handleSubmit() {
    const t = title.trim();
    const b = body.trim();
    if (!t || !b) {
      setError('Add a title and a description so we can act on it.');
      return;
    }
    setBusy(true);
    setError('');
    try {
      const input: CreateFeedbackInput = {
        title: t,
        body: b,
        category,
        ...(platform === 'unspecified' ? {} : { platform }),
      };
      if (isBugOrPerf) {
        if (expectedBehavior.trim()) input.expected_behavior = expectedBehavior.trim();
        if (actualBehavior.trim()) input.actual_behavior = actualBehavior.trim();
        if (stepsToReproduce.trim()) input.steps_to_reproduce = stepsToReproduce.trim();
        if (frequency) input.frequency = frequency;
      }
      if (additionalContext.trim()) input.additional_context = additionalContext.trim();
      if (!excludeDeviceInfo) {
        if (devicePlatform.trim()) input.device_platform = devicePlatform.trim();
        if (browser.trim()) input.browser = browser.trim();
        if (osVersion.trim()) input.os_version = osVersion.trim();
        if (submittedTz.trim()) input.submitted_tz = submittedTz.trim();
        if (Object.keys(snapshot).length > 0) input.device_snapshot = snapshot;
      }
      // Only claim drafts whose image markdown is STILL present in the body
      // (2026-08-10 second adversarial review, finding W2) — deleting the
      // `![alt](url)` text before submit must not silently keep publishing
      // the image anyway. Anything no longer referenced gets revoked
      // instead, once the item is safely created.
      const stillReferenced = insertedDrafts.filter((d) => b.includes(d.url));
      const noLongerReferenced = insertedDrafts.filter((d) => !b.includes(d.url));
      if (stillReferenced.length > 0) {
        input.draft_attachment_ids = stillReferenced.map((d) => d.id);
      }

      const created = await onSubmit(input);
      revokeDrafts(noLongerReferenced.map((d) => d.id));
      if (pendingFiles.length > 0) {
        const { failures, firstError } = await uploadPendingAttachments(created);
        if (failures > 0) {
          const reason = firstError ? ` (${firstError})` : '';
          toast(
            failures === pendingFiles.length
              ? `Feedback posted, but none of your attachments made it${reason}. You can add them from the item's page.`
              : `Feedback posted, but ${failures} of ${pendingFiles.length} attachment${pendingFiles.length === 1 ? '' : 's'} didn't upload${reason}. You can retry from the item's page.`,
          );
        } else {
          toast('Thanks. Your feedback and attachments were posted to the board.');
        }
      } else {
        toast('Thanks. Your feedback was posted to the board.');
      }
      // F2: reconcile LAST — see the docstring on SubmitFeedbackPanelProps.
      await onReconcileAfterCreate(created);
      doClose();
    } catch (err) {
      const structuredMsg = describeStructuredFieldError(err);
      const fieldError = structuredMsg
        ? { message: structuredMsg }
        : err instanceof ApiError
          ? describeFeedbackFieldError(category, err)
          : null;
      if (fieldError) {
        setError(fieldError.message);
        setDiscOpen(true);
      } else {
        setError('Could not post that. Please try again.');
      }
    } finally {
      setBusy(false);
    }
  }

  const bodyMeta = BODY_META[category];
  const snapshotEntries = DEVICE_SNAPSHOT_FIELD_ORDER.filter((k) => snapshot[k] !== undefined);

  return (
    <Sheet
      side="right"
      open={open}
      onClose={requestClose}
      title="Share feedback"
      width="min(480px, 94vw)"
    >
      <PanelBody>
        {confirmDiscardOpen && (
          <DiscardOverlay role="alertdialog" aria-label="Discard your feedback?">
            <DiscardMessage>Discard your feedback? Your draft will be lost.</DiscardMessage>
            <DiscardActions>
              <Button
                ref={keepEditingButtonRef}
                variant="ghost"
                onClick={() => setConfirmDiscardOpen(false)}
              >
                Keep editing
              </Button>
              <Button variant="danger" onClick={handleConfirmedDiscard}>
                Discard
              </Button>
            </DiscardActions>
          </DiscardOverlay>
        )}
        {/* inert while the discard confirm is up (W4) — the form behind the
            overlay must not be focusable/interactive until it's dismissed. */}
        <FormContent inert={confirmDiscardOpen}>
          <Fields>
            <Field>
              <FieldLabel id="feedback-topic-label">Topic</FieldLabel>
              <Topics role="group" aria-labelledby="feedback-topic-label">
                {FEEDBACK_CATEGORIES.map((c) => (
                  <Topic
                    key={c}
                    type="button"
                    $on={category === c}
                    $color={CATEGORY_TONE_COLOR[c]}
                    aria-pressed={category === c}
                    onClick={() => setCategory(c)}
                  >
                    <Icon name={CATEGORY_ICONS[c]} size={12} />
                    {FEEDBACK_CATEGORY_LABELS[c]}
                  </Topic>
                ))}
              </Topics>
            </Field>

            <TextField
              label="Title"
              value={title}
              maxLength={TITLE_MAX}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Short summary, e.g. Feed jumps when images load"
            />

            {(dupSearching || dupResults.length > 0) && (
              <DupBox role="status" aria-live="polite">
                <DupLabel>
                  <Icon name="search" size={13} /> Before you submit
                </DupLabel>
                {dupSearching ? (
                  <Note>Searching…</Note>
                ) : (
                  <DupResults>
                    {dupResults.map((r) => (
                      <DupResult key={r.id}>
                        <DupResultButton type="button" onClick={() => openDuplicate(r.id)}>
                          <DupTitle>{r.title}</DupTitle>
                          <StatusBadge status={r.status} />
                        </DupResultButton>
                      </DupResult>
                    ))}
                  </DupResults>
                )}
              </DupBox>
            )}

            <MarkdownEditor
              label={bodyMeta.label}
              value={body}
              onChange={setBody}
              placeholder={bodyMeta.placeholder}
              maxLength={BODY_MAX}
              rows={6}
              onInsertImage={handleInsertImage}
            />

            <Field>
              <FieldLabel id="feedback-platform-label">Where did this happen?</FieldLabel>
              <PlatformSelect
                aria-labelledby="feedback-platform-label"
                value={platform}
                onChange={(e) => setPlatform(e.target.value as FeedbackPlatform)}
              >
                {(['unspecified', 'web', 'mobile', 'both'] as FeedbackPlatform[]).map((p) => (
                  <option key={p} value={p}>
                    {FEEDBACK_PLATFORM_LABELS[p]}
                  </option>
                ))}
              </PlatformSelect>
            </Field>

            <Disclosure open={discOpen} onToggle={(e) => setDiscOpen(e.currentTarget.open)}>
              <DisclosureSummary>
                <DisclosureChevron>
                  <Icon name="chevron-down" size={12} />
                </DisclosureChevron>
                Add more details (optional)
              </DisclosureSummary>
              <DisclosureBody>
                {isBugOrPerf && (
                  <>
                    <TextField
                      label="Expected behavior"
                      value={expectedBehavior}
                      maxLength={500}
                      onChange={(e) => setExpectedBehavior(e.target.value)}
                    />
                    <TextField
                      label="Actual behavior"
                      value={actualBehavior}
                      maxLength={500}
                      onChange={(e) => setActualBehavior(e.target.value)}
                    />
                    <Textarea
                      label="Steps to reproduce (one per line)"
                      value={stepsToReproduce}
                      maxLength={2000}
                      rows={3}
                      onChange={(e) => setStepsToReproduce(e.target.value)}
                    />
                    <Field>
                      <FieldLabel id="feedback-frequency-label">
                        How often does it happen?
                      </FieldLabel>
                      <RadioRow role="radiogroup" aria-labelledby="feedback-frequency-label">
                        {FREQUENCY_OPTIONS.map((opt) => (
                          <RadioOption key={opt.value} $on={frequency === opt.value}>
                            <input
                              type="radio"
                              name="feedback-frequency"
                              value={opt.value}
                              checked={frequency === opt.value}
                              onChange={() => setFrequency(opt.value)}
                              onClick={() => {
                                if (frequency === opt.value) setFrequency('');
                              }}
                            />
                            {opt.label}
                          </RadioOption>
                        ))}
                      </RadioRow>
                    </Field>
                  </>
                )}

                <Textarea
                  label="Additional context"
                  value={additionalContext}
                  maxLength={1000}
                  rows={3}
                  onChange={(e) => setAdditionalContext(e.target.value)}
                />

                <Field>
                  <FieldLabel>Device &amp; environment</FieldLabel>
                  <Note>
                    Read from your browser just now, sent only if you submit this feedback, never
                    stored otherwise.
                  </Note>
                  <Switch
                    checked={excludeDeviceInfo}
                    onChange={setExcludeDeviceInfo}
                    label="Don't include device info"
                  />
                  {!excludeDeviceInfo && (
                    <>
                      <DeviceRow>
                        <TextField
                          label="Platform"
                          value={devicePlatform}
                          onChange={(e) => setDevicePlatform(e.target.value)}
                        />
                        <RemoveFieldButton
                          type="button"
                          aria-label="Remove platform"
                          onClick={() => setDevicePlatform('')}
                        >
                          <Icon name="close" size={13} />
                        </RemoveFieldButton>
                      </DeviceRow>
                      <DeviceRow>
                        <TextField
                          label="Browser"
                          value={browser}
                          onChange={(e) => setBrowser(e.target.value)}
                        />
                        <RemoveFieldButton
                          type="button"
                          aria-label="Remove browser"
                          onClick={() => setBrowser('')}
                        >
                          <Icon name="close" size={13} />
                        </RemoveFieldButton>
                      </DeviceRow>
                      <DeviceRow>
                        <TextField
                          label="OS version"
                          value={osVersion}
                          onChange={(e) => setOsVersion(e.target.value)}
                        />
                        <RemoveFieldButton
                          type="button"
                          aria-label="Remove OS version"
                          onClick={() => setOsVersion('')}
                        >
                          <Icon name="close" size={13} />
                        </RemoveFieldButton>
                      </DeviceRow>
                      <DeviceRow>
                        <TextField
                          label="Your local time zone"
                          value={submittedTz}
                          onChange={(e) => setSubmittedTz(e.target.value)}
                        />
                        <RemoveFieldButton
                          type="button"
                          aria-label="Remove time zone"
                          onClick={() => setSubmittedTz('')}
                        >
                          <Icon name="close" size={13} />
                        </RemoveFieldButton>
                      </DeviceRow>

                      {snapshotEntries.length > 0 && (
                        <SnapshotList aria-label="Captured device details">
                          {snapshotEntries.map((key) => (
                            <SnapshotRow key={key}>
                              <span>
                                {DEVICE_SNAPSHOT_FIELD_LABELS[key]}: {String(snapshot[key])}
                              </span>
                              <RemoveFieldButton
                                type="button"
                                aria-label={`Remove ${DEVICE_SNAPSHOT_FIELD_LABELS[key]}`}
                                onClick={() => removeSnapshotField(key)}
                              >
                                <Icon name="close" size={13} />
                              </RemoveFieldButton>
                            </SnapshotRow>
                          ))}
                        </SnapshotList>
                      )}
                    </>
                  )}
                </Field>
              </DisclosureBody>
            </Disclosure>

            <Field>
              <FieldLabel>Attachments (optional)</FieldLabel>
              {pendingFiles.length > 0 && (
                <AttachList aria-label="Files to attach">
                  {pendingFiles.map((f, i) => (
                    <AttachRow key={`${f.name}-${i}`}>
                      <AttachName>{f.name}</AttachName>
                      <AttachRemoveButton
                        type="button"
                        aria-label={`Remove ${f.name}`}
                        onClick={() => removePendingFile(i)}
                      >
                        <Icon name="close" size={14} />
                      </AttachRemoveButton>
                    </AttachRow>
                  ))}
                </AttachList>
              )}
              {totalAttachments < MAX_ATTACHMENTS && (
                <AttachButton variant="ghost" onClick={() => fileInputRef.current?.click()}>
                  <Icon name="paperclip" size={14} />
                  Add files
                </AttachButton>
              )}
              <input
                ref={fileInputRef}
                type="file"
                multiple
                accept={ATTACHMENT_ACCEPT}
                hidden
                onChange={handlePickFiles}
                aria-hidden="true"
                tabIndex={-1}
              />
              {attachError && <ErrorText role="alert">{attachError}</ErrorText>}
            </Field>

            <Note>
              Posts to the public board, where anyone can upvote it. For threats, scams or abuse use
              Report on the post itself. That goes to the Council, not here.
            </Note>

            {error && <ErrorText role="alert">{error}</ErrorText>}
          </Fields>

          <Actions>
            <Button variant="ghost" onClick={requestClose}>
              Cancel
            </Button>
            <Button variant="primary" onClick={() => void handleSubmit()} disabled={busy}>
              {busy ? 'Posting…' : 'Post feedback'}
            </Button>
          </Actions>
        </FormContent>
      </PanelBody>
    </Sheet>
  );
}
